import React, { useState, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Dialog, DialogContent, CircularProgress } from '@mui/material'
import { PageLayout, PageHeader } from '../../components'
import { StatusChip, LoadingSpinner, StatCard, ToastNotification } from '../../components/atoms'
import useBillingDashboardData from '../../hooks/useBillingDashboardData'
import useBillingInvoiceData from '../../hooks/useBillingInvoiceData'
import { useToast } from '../../hooks'
import { authenticatedFetch, authenticatedFetchJson } from '../../services/api/apiClient'
import { apiEndpoints } from '../../consts'
import logger from '../../utils/logger'
import FinalizeAllDraftsDialog from './FinalizeAllDraftsDialog'
import BillingPausedBanner from '../../components/BillingPausedBanner'
import StripeRailChip from '../../components/atoms/StripeRailChip'
import humanizeKey from '../../utils/humanizeKey'
import styles from './AdminBillingDashboard.module.scss'

const INVOICE_STATUS_COLORS = {
  draft: 'neutral',
  pending: 'warning',
  paid: 'success',
  overdue: 'error',
  cancelled: 'neutral',
  refunded: 'info',
  partially_refunded: 'info',
}

const INVOICE_STATUS_LABELS = {
  draft: 'Draft',
  pending: 'Pending',
  paid: 'Paid',
  overdue: 'Overdue',
  cancelled: 'Cancelled',
  refunded: 'Refunded',
  partially_refunded: 'Partial Refund',
}

const STATUS_FILTER_OPTIONS = [
  'all',
  'draft',
  'pending',
  'paid',
  'overdue',
  'cancelled',
  'refunded',
  'partially_refunded',
]
const TYPE_FILTER_OPTIONS = ['all', 'base', 'overage', 'deposit']
const PAGE_SIZE = 25

const formatCurrency = (minorAmount, currency = 'USD') => {
  const amount = (minorAmount || 0) / 100
  return new window.Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount)
}

const formatDate = (dateStr) => {
  if (!dateStr) return '\u2014'
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

const PAYMENT_METHOD_LABELS = {
  credit_card: 'Credit Card',
  eft: 'EFT',
  wire: 'Wire Transfer',
  pad: 'PAD (Pre-Authorized Debit)',
  ach: 'ACH',
  other: 'Manual / Other',
  deposit_credit: 'Deposit Credit',
}

const formatPaymentMethod = (paidBy) => {
  if (!paidBy) return 'Stripe (Auto-Charge)'
  return PAYMENT_METHOD_LABELS[paidBy] || paidBy
}

// Schema of payment_method_details varies per type — narrow on `type`.
const formatPaymentMethodSummary = (raw) => {
  if (!raw) return null
  let pm = raw
  if (typeof raw === 'string') {
    try {
      pm = JSON.parse(raw)
    } catch (err) {
      return null
    }
  }
  if (!pm || typeof pm !== 'object' || !pm.type) return null

  if (pm.type === 'card' && pm.card) {
    const c = pm.card
    const brand = c.brand ? c.brand.charAt(0).toUpperCase() + c.brand.slice(1) : 'Card'
    const last4 = c.last4 ? `•••• ${c.last4}` : ''
    const exp =
      c.exp_month && c.exp_year
        ? ` · exp ${String(c.exp_month).padStart(2, '0')}/${String(c.exp_year).slice(-2)}`
        : ''
    const country = c.country ? ` · ${c.country}` : ''
    const funding = c.funding ? ` · ${c.funding}` : ''
    return `${brand} ${last4}${exp}${country}${funding}`.trim()
  }
  if (pm.type === 'us_bank_account' && pm.us_bank_account) {
    const b = pm.us_bank_account
    const bank = b.bank_name || 'US Bank'
    const last4 = b.last4 ? ` •••• ${b.last4}` : ''
    const acctType = b.account_type ? ` · ${b.account_type}` : ''
    return `${bank}${last4}${acctType}`.trim()
  }
  if (pm.type === 'acss_debit' && pm.acss_debit) {
    const b = pm.acss_debit
    const bank = b.bank_name || 'CA Bank (ACSS)'
    const last4 = b.last4 ? ` •••• ${b.last4}` : ''
    return `${bank}${last4}`.trim()
  }
  return pm.type.replace(/_/g, ' ')
}

const AUDIT_ACTION_LABELS = {
  created: 'Invoice Created',
  imported: 'Imported from Stripe',
  finalized: 'Finalized',
  approved: 'Approved & Sent',
  draft_edited: 'Draft Edited',
  marked_paid: 'Marked as Paid',
  cancelled: 'Cancelled',
  full_refund: 'Full Refund',
  refunded: 'Full Refund',
  partial_refund: 'Partial Refund',
  edited: 'Edited',
  revision_created: 'Revision Created',
  reminder_sent: 'Reminder Sent',
  email_sent: 'Email Sent',
  status_change: 'Status Changed',
}

const formatAuditAction = (action) => {
  return (
    AUDIT_ACTION_LABELS[action] ||
    action.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  )
}

const SortableHeader = ({ field, label, sortField, sortDirection, onSort }) => (
  <th className={styles.sortable}>
    <button type="button" className={styles.sortButton} onClick={() => onSort(field)}>
      {label} {sortField === field && (sortDirection === 'asc' ? '\u2191' : '\u2193')}
    </button>
  </th>
)

const ACTION_CONFIG = {
  'mark-paid': {
    title: 'Mark Invoice as Paid',
    confirmLabel: 'Mark Paid',
    confirmColor: 'primary',
    fields: [
      {
        key: 'payment_method',
        label: 'Payment Method',
        required: true,
        type: 'select',
        options: [
          { value: 'credit_card', label: 'Credit Card' },
          { value: 'eft', label: 'EFT' },
          { value: 'wire', label: 'Wire Transfer' },
          { value: 'pad', label: 'PAD (Canadian Bank)' },
          { value: 'ach', label: 'ACH (US Bank)' },
          { value: 'deposit_credit', label: 'Deposit Credit' },
          { value: 'other', label: 'Other' },
        ],
      },
      { key: 'payment_confirmation', label: 'Payment Reference / Confirmation', required: true },
      {
        key: 'method',
        label: 'Settlement Method (optional — for off-Stripe payments only)',
        required: false,
        type: 'select',
        options: [
          { value: 'wire', label: 'Wire Transfer' },
          { value: 'check', label: 'Check' },
          { value: 'manual_ach', label: 'Manual ACH' },
          { value: 'other', label: 'Other' },
        ],
      },
      { key: 'reference', label: 'Settlement Reference', required: false },
      { key: 'notes', label: 'Payment Notes', required: true, multiline: true },
    ],
  },
  cancel: {
    title: 'Cancel Invoice',
    confirmLabel: 'Cancel Invoice',
    confirmColor: 'error',
    fields: [{ key: 'reason', label: 'Cancellation Reason', required: true, multiline: true }],
  },
  refund: {
    title: 'Process Refund',
    confirmLabel: 'Process Refund',
    confirmColor: 'error',
    fields: [
      {
        key: 'refund_amount',
        label: 'Refund Amount ($)',
        required: false,
        type: 'number',
        helperText: 'Leave blank for full refund',
        inputProps: { min: 0, step: '0.01' },
      },
      { key: 'reason', label: 'Refund Reason', required: true, multiline: true },
    ],
  },
  approve: {
    title: 'Approve & Send Invoice',
    confirmLabel: 'Approve & Send',
    confirmColor: 'primary',
    fields: [
      { key: 'notes', label: 'Approval Notes (optional)', required: false, multiline: true },
    ],
  },
  'edit-draft': {
    title: 'Edit Draft Invoice',
    confirmLabel: 'Save Changes',
    confirmColor: 'primary',
    fields: [
      {
        key: 'subtotal',
        label: 'New Subtotal ($)',
        required: false,
        type: 'number',
        helperText: 'Tax auto-recalculates. Leave blank to keep current.',
        inputProps: { min: 0, step: '0.01' },
      },
      {
        key: 'total_override',
        label: 'Total Override ($)',
        required: false,
        type: 'number',
        helperText: 'Overrides auto-calculated total. Leave blank for auto.',
        inputProps: { min: 0, step: '0.01' },
      },
      { key: 'description', label: 'Description', required: false, multiline: true },
      { key: 'notes', label: 'Notes', required: false, multiline: true },
    ],
  },
}

const InvoiceActionDialog = ({ open, action, invoice, onClose, onComplete }) => {
  const config = ACTION_CONFIG[action]
  const [formData, setFormData] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [referenceError, setReferenceError] = useState(null)

  React.useEffect(() => {
    if (open) {
      setFormData({})
      setError(null)
      setReferenceError(null)
    }
  }, [open])

  const handleSubmit = useCallback(async () => {
    if (!config || !invoice) return
    for (const field of config.fields) {
      if (field.required && !formData[field.key]?.toString().trim()) {
        setError(`${field.label} is required`)
        return
      }
    }
    setSubmitting(true)
    setError(null)
    setReferenceError(null)
    try {
      if (action === 'mark-paid') {
        const hasMethod = Boolean(formData.method?.trim())
        const hasReference = Boolean(formData.reference?.trim())
        if (hasMethod !== hasReference) {
          setError('Settlement Method and Reference must both be set together')
          setSubmitting(false)
          return
        }
        const markPaidBody = {
          payment_method: formData.payment_method,
          payment_confirmation: formData.payment_confirmation,
          notes: formData.notes,
        }
        if (hasMethod && hasReference) {
          markPaidBody.method = formData.method.trim()
          markPaidBody.reference = formData.reference.trim()
        }
        await authenticatedFetchJson(apiEndpoints.billingInvoice(invoice.id) + '/mark-paid', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(markPaidBody),
        })
      } else if (action === 'cancel') {
        await authenticatedFetchJson(apiEndpoints.billingInvoice(invoice.id) + '/cancel', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: formData.reason }),
        })
      } else if (action === 'refund') {
        const body = { reason: formData.reason }
        if (formData.refund_amount) {
          const dollars = parseFloat(formData.refund_amount)
          if (isNaN(dollars) || dollars <= 0) {
            setError('Refund amount must be a positive number')
            setSubmitting(false)
            return
          }
          body.amount_minor = Math.round(dollars * 100)
        }
        await authenticatedFetchJson(apiEndpoints.billingInvoice(invoice.id) + '/refund', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        })
      } else if (action === 'approve') {
        await authenticatedFetchJson(apiEndpoints.billingInvoice(invoice.id) + '/approve', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ notes: formData.notes }),
        })
      } else if (action === 'edit-draft') {
        const body = {}
        if (formData.subtotal) {
          const dollars = parseFloat(formData.subtotal)
          if (isNaN(dollars) || dollars < 0) {
            setError('Subtotal must be a non-negative number')
            setSubmitting(false)
            return
          }
          body.subtotal_minor = Math.round(dollars * 100)
        }
        if (formData.total_override) {
          const dollars = parseFloat(formData.total_override)
          if (isNaN(dollars) || dollars < 0) {
            setError('Total override must be a non-negative number')
            setSubmitting(false)
            return
          }
          body.total_minor_override = Math.round(dollars * 100)
        }
        if (formData.description) body.description = formData.description
        if (formData.notes) body.notes = formData.notes
        await authenticatedFetchJson(apiEndpoints.billingInvoice(invoice.id) + '/draft', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        })
      }
      onComplete()
    } catch (err) {
      const msg = err.message || 'Action failed'
      if (action === 'mark-paid' && msg.includes('already recorded')) {
        setReferenceError('Payment with this reference already recorded')
      } else if (action === 'refund' && msg.includes('no payment_intent or charge')) {
        const method = invoice?.paid_by
        if (method === 'pad' || method === 'ach') {
          setError(
            `This invoice was paid via ${formatPaymentMethod(method)}. PAD/ACH payments cannot be refunded through Stripe — process the refund through your bank.`,
          )
        } else if (method === 'other' || method === 'wire' || method === 'eft') {
          setError(
            `This invoice was marked as paid via ${formatPaymentMethod(method)}. Since the payment was not processed through Stripe, it cannot be refunded here — process the refund manually.`,
          )
        } else {
          setError(
            'This invoice has no Stripe payment record. It may have been paid out-of-band or imported. Process the refund manually.',
          )
        }
      } else {
        setError(msg)
      }
    } finally {
      setSubmitting(false)
    }
  }, [action, config, formData, invoice, onComplete])

  if (!config) return null

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      data-testid="action-dialog"
      className={styles.actionDialog}
    >
      <div className={styles.dialogHeader}>
        <span className={styles.dialogTitle}>{config.title}</span>
        <button
          className={styles.dialogClose}
          onClick={onClose}
          disabled={submitting}
          aria-label="Close"
        >
          &times;
        </button>
      </div>
      <DialogContent className={styles.dialogContent}>
        {invoice && (
          <div className={styles.invoiceChip}>
            <span className={styles.invoiceChipLabel}>{invoice.invoice_number_local}</span>
            <span className={styles.invoiceChipAmount}>
              {formatCurrency(invoice.total_minor, invoice.currency)}
            </span>
          </div>
        )}
        {action === 'refund' && invoice && (
          <p className={styles.refundHint}>
            Maximum refundable:{' '}
            <strong>
              {formatCurrency(
                invoice.total_minor - (invoice.amount_refunded_minor || 0),
                invoice.currency,
              )}
            </strong>
          </p>
        )}
        {config.fields.map((field) => (
          <div key={field.key} className={styles.fieldGroup}>
            <label className={styles.fieldLabel}>
              {field.label}
              {field.required && <span className={styles.fieldRequired}>*</span>}
            </label>
            {field.multiline ? (
              <textarea
                className={styles.fieldInput}
                value={formData[field.key] || ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, [field.key]: e.target.value }))}
                rows={3}
                placeholder={field.helperText || ''}
              />
            ) : field.type === 'select' ? (
              <select
                className={styles.fieldInput}
                value={formData[field.key] || ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, [field.key]: e.target.value }))}
              >
                <option value="">Select…</option>
                {field.options?.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            ) : (
              <input
                className={styles.fieldInput}
                value={formData[field.key] || ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, [field.key]: e.target.value }))}
                type={field.type || 'text'}
                placeholder={field.helperText || ''}
                min={field.inputProps?.min}
                step={field.inputProps?.step}
              />
            )}
            {field.key === 'reference' && referenceError && (
              <div className={styles.fieldError} data-testid="reference-error">{referenceError}</div>
            )}
          </div>
        ))}
        {error && <div className={styles.dialogError}>{error}</div>}
      </DialogContent>
      <div className={styles.dialogActions}>
        <button className={styles.cancelBtn} onClick={onClose} disabled={submitting}>
          Cancel
        </button>
        <button
          className={`${styles.confirmBtn} ${config.confirmColor === 'error' ? styles.confirmDanger : styles.confirmPrimary}`}
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting && (
            <CircularProgress size={14} style={{ marginRight: 8, color: 'inherit' }} />
          )}
          {config.confirmLabel}
        </button>
      </div>
    </Dialog>
  )
}

const InvoiceDetailDrawer = ({ invoice, onClose, onAction, showToast }) => {
  const [detail, setDetail] = useState(null)
  const [loading, setLoading] = useState(true)
  const [actionDialog, setActionDialog] = useState(null)
  const [resendConfirmOpen, setResendConfirmOpen] = useState(false)
  const [resending, setResending] = useState(false)

  React.useEffect(() => {
    if (!invoice) return
    setLoading(true)
    authenticatedFetchJson(apiEndpoints.billingInvoice(invoice.id))
      .then((res) => setDetail(res?.data || null))
      .catch((err) => logger.error('Failed to load invoice detail:', err))
      .finally(() => setLoading(false))
  }, [invoice])

  const handleResend = useCallback(async () => {
    if (!detail) return
    setResendConfirmOpen(false)
    setResending(true)
    try {
      await authenticatedFetchJson(apiEndpoints.billingInvoice(detail.id) + '/resend', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      })
      if (showToast) showToast('Invoice email resent successfully', 'success')
      if (onAction) onAction()
    } catch (err) {
      logger.error('Resend failed:', err)
      if (showToast) showToast(err.message || 'Failed to resend invoice email', 'error')
    } finally {
      setResending(false)
    }
  }, [detail, onAction, showToast])

  const handleActionComplete = useCallback(() => {
    setActionDialog(null)
    if (onAction) onAction()
  }, [onAction])

  if (!invoice) return null

  const isDraft = detail && detail.status === 'draft'
  const isEditable = detail && ['pending', 'overdue'].includes(detail.status)
  const isRefundable = detail && ['paid', 'partially_refunded'].includes(detail.status)

  return (
    <div className={styles.drawerOverlay} onClick={onClose} data-testid="invoice-drawer">
      <div className={styles.drawer} onClick={(e) => e.stopPropagation()}>
        <div className={styles.drawerHeader}>
          <h2 className={styles.drawerTitle}>
            {invoice.invoice_number_local || 'Invoice Detail'}
            {detail?.stripe_invoice_number &&
              detail.stripe_invoice_number !== invoice.invoice_number_local && (
                <span className={styles.drawerStripeNumber}>
                  Stripe: {detail.stripe_invoice_number}
                </span>
              )}
          </h2>
          <button className={styles.drawerClose} onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>

        {loading ? (
          <LoadingSpinner message="Loading invoice..." />
        ) : detail ? (
          <>
            <div className={styles.drawerContent}>
              {/* Summary */}
              <div className={styles.drawerSection}>
                <div className={styles.drawerRow}>
                  <span className={styles.drawerLabel}>Status</span>
                  <StatusChip
                    label={INVOICE_STATUS_LABELS[detail.status] || detail.status}
                    color={INVOICE_STATUS_COLORS[detail.status] || 'neutral'}
                    size="small"
                  />
                </div>
                <div className={styles.drawerRow}>
                  <span className={styles.drawerLabel}>Type</span>
                  <span className={styles.drawerValue}>{detail.invoice_type}</span>
                </div>
                <div className={styles.drawerRow}>
                  <span className={styles.drawerLabel}>Due Date</span>
                  <span className={styles.drawerValue}>{formatDate(detail.due_date)}</span>
                </div>
                {(detail.billing_period_start || detail.billing_period_end) && (
                  <div className={styles.drawerRow}>
                    <span className={styles.drawerLabel}>Period</span>
                    <span className={styles.drawerValue}>
                      {formatDate(detail.billing_period_start)} &ndash;{' '}
                      {formatDate(detail.billing_period_end)}
                    </span>
                  </div>
                )}
                {['paid', 'partially_refunded', 'refunded'].includes(detail.status) && (
                  <>
                    {detail.paid_at && (
                      <div className={styles.drawerRow}>
                        <span className={styles.drawerLabel}>Paid At</span>
                        <span className={styles.drawerValue}>{formatDate(detail.paid_at)}</span>
                      </div>
                    )}
                    <div className={styles.drawerRow}>
                      <span className={styles.drawerLabel}>Payment Method</span>
                      <span className={styles.drawerValue}>
                        {formatPaymentMethod(detail.paid_by)}
                      </span>
                    </div>
                  </>
                )}
              </div>

              {/* Financial Summary */}
              <div className={styles.drawerSection}>
                <h3 className={styles.drawerSectionTitle}>Financial Summary</h3>
                <div className={styles.financialSummary}>
                  {(() => {
                    // Other gets folded into subtotal_minor for the math model
                    // (base + other → 5% surcharge → tax cascade). When Other
                    // is non-zero, surface the unfolded base separately so the
                    // ledger reads cleanly: Base + Other = Subtotal — instead
                    // of "Subtotal $400 / Other $100" which reads as $500.
                    const otherMinor = detail.other_minor || 0
                    const otherRemarks = detail.other_remarks || ''
                    const hasOther = otherMinor > 0 || otherRemarks.length > 0
                    if (hasOther) {
                      const baseOnly = (detail.subtotal_minor || 0) - otherMinor
                      return (
                        <>
                          <div className={styles.financialRow}>
                            <span>Base</span>
                            <span>{formatCurrency(baseOnly, detail.currency)}</span>
                          </div>
                          <div className={styles.financialRow}>
                            <span>{otherRemarks ? `Other: ${otherRemarks}` : 'Other'}</span>
                            <span>{formatCurrency(otherMinor, detail.currency)}</span>
                          </div>
                          <div className={styles.financialRow}>
                            <span>Subtotal</span>
                            <span>{formatCurrency(detail.subtotal_minor, detail.currency)}</span>
                          </div>
                        </>
                      )
                    }
                    return (
                      <div className={styles.financialRow}>
                        <span>Subtotal</span>
                        <span>{formatCurrency(detail.subtotal_minor, detail.currency)}</span>
                      </div>
                    )
                  })()}
                  {detail.surcharge_minor > 0 && (
                    <div className={styles.financialRow}>
                      <span>Platform Enhancement (5%)</span>
                      <span>{formatCurrency(detail.surcharge_minor, detail.currency)}</span>
                    </div>
                  )}
                  {(detail.tax_post_minor || detail.tax_minor) > 0 && (
                    <div className={styles.financialRow}>
                      <span>
                        Tax{' '}
                        {detail.tax_percent_basis_points
                          ? `(${(detail.tax_percent_basis_points / 100).toFixed(1)}%)`
                          : ''}
                      </span>
                      <span>
                        {formatCurrency(detail.tax_post_minor || detail.tax_minor, detail.currency)}
                      </span>
                    </div>
                  )}
                  {(() => {
                    const isBank =
                      detail.preferred_payment_method === 'pad' ||
                      detail.preferred_payment_method === 'ach'
                    const totalPost = detail.total_post_minor || detail.total_minor
                    const totalPre = detail.total_minor
                    const discount = (totalPost || 0) - (totalPre || 0)
                    if (isBank && discount > 0) {
                      return (
                        <>
                          <div className={styles.financialRow}>
                            <span>Invoice Total</span>
                            <span>{formatCurrency(totalPost, detail.currency)}</span>
                          </div>
                          <div className={styles.financialRow}>
                            <span>Bank Payment Discount</span>
                            <span className={styles.financialNegative}>
                              &minus;{formatCurrency(discount, detail.currency)}
                            </span>
                          </div>
                          <div className={`${styles.financialRow} ${styles.financialTotal}`}>
                            <span>Charged Amount</span>
                            <span>{formatCurrency(totalPre, detail.currency)}</span>
                          </div>
                        </>
                      )
                    }
                    return (
                      <div className={`${styles.financialRow} ${styles.financialTotal}`}>
                        <span>Charged Amount</span>
                        <span>{formatCurrency(totalPost || totalPre, detail.currency)}</span>
                      </div>
                    )
                  })()}
                  {detail.amount_paid_minor > 0 && (
                    <div className={styles.financialRow}>
                      <span>Amount Paid</span>
                      <span className={styles.financialPositive}>
                        {formatCurrency(detail.amount_paid_minor, detail.currency)}
                      </span>
                    </div>
                  )}
                  {detail.amount_refunded_minor > 0 && (
                    <div className={styles.financialRow}>
                      <span>Amount Refunded</span>
                      <span className={styles.financialNegative}>
                        &minus;{formatCurrency(detail.amount_refunded_minor, detail.currency)}
                      </span>
                    </div>
                  )}
                  {detail.amount_refunded_minor > 0 && (
                    <div className={`${styles.financialRow} ${styles.financialNet}`}>
                      <span>Net Collected</span>
                      <span>
                        {formatCurrency(
                          detail.amount_paid_minor - detail.amount_refunded_minor,
                          detail.currency,
                        )}
                      </span>
                    </div>
                  )}
                  {detail.status === 'pending' || detail.status === 'overdue' ? (
                    <div className={`${styles.financialRow} ${styles.financialOutstanding}`}>
                      <span>Outstanding Balance</span>
                      <span>
                        {formatCurrency(
                          detail.total_minor - (detail.amount_paid_minor || 0),
                          detail.currency,
                        )}
                      </span>
                    </div>
                  ) : null}
                </div>
              </div>

              {(detail.stripe_payment_intent_id ||
                detail.stripe_invoice_id ||
                detail.stripe_charge_id ||
                detail.stripe_receipt_url ||
                detail.stripe_receipt_number ||
                detail.stripe_payment_method_summary_json ||
                detail.stripe_fee_minor != null ||
                detail.stripe_net_minor != null ||
                detail.stripe_description ||
                detail.stripe_receipt_email ||
                detail.stripe_risk_level ||
                detail.stripe_statement_descriptor ||
                detail.stripe_metadata_json) && (
                <div className={styles.drawerSection} data-testid="invoice-stripe-details">
                  <h3 className={styles.drawerSectionTitle}>Stripe Details</h3>
                  {detail.stripe_payment_intent_id && (
                    <div className={styles.drawerRow}>
                      <span className={styles.drawerLabel}>PaymentIntent ID</span>
                      <span className={styles.drawerValue}>
                        <code>{detail.stripe_payment_intent_id}</code>
                      </span>
                    </div>
                  )}
                  {detail.stripe_invoice_id && (
                    <div className={styles.drawerRow}>
                      <span className={styles.drawerLabel}>Stripe Invoice ID</span>
                      <span className={styles.drawerValue}>
                        <code>{detail.stripe_invoice_id}</code>
                      </span>
                    </div>
                  )}
                  {detail.stripe_charge_id && (
                    <div className={styles.drawerRow}>
                      <span className={styles.drawerLabel}>Charge ID</span>
                      <span className={styles.drawerValue}>
                        <code>{detail.stripe_charge_id}</code>
                      </span>
                    </div>
                  )}
                  {detail.stripe_receipt_number && (
                    <div className={styles.drawerRow}>
                      <span className={styles.drawerLabel}>Receipt Number</span>
                      <span className={styles.drawerValue}>{detail.stripe_receipt_number}</span>
                    </div>
                  )}
                  {detail.stripe_payment_method_summary_json && (
                    <div className={styles.drawerRow}>
                      <span className={styles.drawerLabel}>Payment Method</span>
                      <span className={styles.drawerValue}>
                        {formatPaymentMethodSummary(detail.stripe_payment_method_summary_json) ||
                          '—'}
                      </span>
                    </div>
                  )}
                  {detail.stripe_fee_minor != null && (
                    <div className={styles.drawerRow}>
                      <span className={styles.drawerLabel}>Stripe Fee</span>
                      <span className={styles.drawerValue}>
                        {formatCurrency(
                          detail.stripe_fee_minor,
                          detail.stripe_balance_currency || detail.currency,
                        )}
                        {detail.stripe_balance_currency &&
                          detail.stripe_balance_currency !== detail.currency && (
                            <span className={styles.drawerCurrencyHint}>
                              {' '}
                              · settled in {detail.stripe_balance_currency}
                            </span>
                          )}
                      </span>
                    </div>
                  )}
                  {detail.stripe_net_minor != null && (
                    <div className={styles.drawerRow}>
                      <span className={styles.drawerLabel}>Net Deposited</span>
                      <span className={styles.drawerValue}>
                        {formatCurrency(
                          detail.stripe_net_minor,
                          detail.stripe_balance_currency || detail.currency,
                        )}
                        {detail.stripe_balance_currency &&
                          detail.stripe_balance_currency !== detail.currency && (
                            <span className={styles.drawerCurrencyHint}>
                              {' '}
                              · settled in {detail.stripe_balance_currency}
                            </span>
                          )}
                      </span>
                    </div>
                  )}
                  {detail.stripe_receipt_url && (
                    <div className={styles.drawerRow}>
                      <span className={styles.drawerLabel}>Receipt</span>
                      <span className={styles.drawerValue}>
                        <a
                          href={detail.stripe_receipt_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="invoice-stripe-receipt-link"
                        >
                          View on Stripe <FontAwesomeIcon icon="external-link-alt" />
                        </a>
                      </span>
                    </div>
                  )}
                  {detail.stripe_description && (
                    <div className={styles.drawerRow}>
                      <span className={styles.drawerLabel}>Description</span>
                      <span className={styles.drawerValue}>{detail.stripe_description}</span>
                    </div>
                  )}
                  {detail.stripe_receipt_email && (
                    <div className={styles.drawerRow}>
                      <span className={styles.drawerLabel}>Receipt Email</span>
                      <span className={styles.drawerValue}>
                        <a href={`mailto:${detail.stripe_receipt_email}`}>
                          {detail.stripe_receipt_email}
                        </a>
                      </span>
                    </div>
                  )}
                  {detail.stripe_statement_descriptor && (
                    <div className={styles.drawerRow}>
                      <span className={styles.drawerLabel}>Statement Descriptor</span>
                      <span className={styles.drawerValue}>
                        <code>{detail.stripe_statement_descriptor}</code>
                      </span>
                    </div>
                  )}
                  {detail.stripe_risk_level && (
                    <div className={styles.drawerRow}>
                      <span className={styles.drawerLabel}>Risk</span>
                      <span
                        className={`${styles.drawerValue} ${styles[`risk_${detail.stripe_risk_level}`] || ''}`}
                      >
                        {detail.stripe_risk_level}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {detail.stripe_metadata_json &&
                Object.keys(detail.stripe_metadata_json).length > 0 && (
                  <div className={styles.drawerSection} data-testid="invoice-stripe-metadata">
                    <h3 className={styles.drawerSectionTitle}>Stripe Metadata</h3>
                    <table className={styles.metadataTable}>
                      <tbody>
                        {Object.entries(detail.stripe_metadata_json)
                          .sort(([a], [b]) => a.localeCompare(b))
                          .map(([key, value]) => (
                            <tr key={key}>
                              <td className={styles.metadataKey}>{humanizeKey(key)}</td>
                              <td className={styles.metadataValue}>
                                {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}

              {/* Line Items */}
              {detail.line_items?.length > 0 && (
                <div className={styles.drawerSection}>
                  <h3 className={styles.drawerSectionTitle}>Line Items</h3>
                  <table className={styles.lineItemsTable}>
                    <thead>
                      <tr>
                        <th>Description</th>
                        <th>Qty</th>
                        <th>Rate</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.line_items.map((item) => (
                        <tr key={item.id}>
                          <td>{item.description}</td>
                          <td>{item.quantity}</td>
                          <td>{formatCurrency(item.per_unit_cost_minor, detail.currency)}</td>
                          <td>{formatCurrency(item.subtotal_minor, detail.currency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Audit Log */}
              {detail.audit_log?.length > 0 && (
                <div className={styles.drawerSection}>
                  <h3 className={styles.drawerSectionTitle}>Audit Log</h3>
                  <div className={styles.auditList}>
                    {detail.audit_log.map((entry) => {
                      const oldVals = entry.old_values
                        ? typeof entry.old_values === 'string'
                          ? JSON.parse(entry.old_values)
                          : entry.old_values
                        : {}
                      const newVals = entry.new_values
                        ? typeof entry.new_values === 'string'
                          ? JSON.parse(entry.new_values)
                          : entry.new_values
                        : {}
                      const isRefundEntry = ['partial_refund', 'full_refund', 'refunded'].includes(
                        entry.action,
                      )
                      const refundThisAction =
                        newVals.refund_amount_minor ||
                        (isRefundEntry
                          ? (newVals.amount_refunded_minor || 0) -
                            (oldVals.amount_refunded_minor || 0)
                          : 0)
                      return (
                        <div key={entry.id} className={styles.auditEntry}>
                          <div className={styles.auditHeader}>
                            <span className={styles.auditAction}>
                              {formatAuditAction(entry.action)}
                            </span>
                            {isRefundEntry && refundThisAction > 0 && (
                              <span className={styles.auditAmount}>
                                &minus;{formatCurrency(refundThisAction, detail.currency)}
                              </span>
                            )}
                            {!isRefundEntry && newVals.total_minor > 0 && (
                              <span className={styles.auditAmount}>
                                {formatCurrency(newVals.total_minor, detail.currency)}
                              </span>
                            )}
                          </div>
                          {isRefundEntry &&
                            (oldVals.amount_refunded_minor > 0 ||
                              newVals.amount_refunded_minor > 0) && (
                              <div className={styles.auditRefundDetail}>
                                <span>
                                  Previously refunded:{' '}
                                  {formatCurrency(
                                    oldVals.amount_refunded_minor || 0,
                                    detail.currency,
                                  )}
                                </span>
                                <span className={styles.auditRefundArrow}>&rarr;</span>
                                <span>
                                  Total refunded:{' '}
                                  {formatCurrency(
                                    newVals.amount_refunded_minor || 0,
                                    detail.currency,
                                  )}
                                </span>
                              </div>
                            )}
                          <span className={styles.auditMeta}>
                            {entry.performed_by || 'System'} &middot; {formatDate(entry.created_at)}
                          </span>
                          {entry.reason && (
                            <span className={styles.auditReason}>{entry.reason}</span>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Email History */}
              {detail.email_history?.length > 0 && (
                <div className={styles.drawerSection}>
                  <h3 className={styles.drawerSectionTitle}>Email History</h3>
                  <div className={styles.auditList}>
                    {detail.email_history.map((entry) => (
                      <div key={entry.id} className={styles.auditEntry}>
                        <span className={styles.auditAction}>{entry.action || 'sent'}</span>
                        <span className={styles.auditMeta}>
                          {entry.recipients
                            ? Array.isArray(entry.recipients)
                              ? entry.recipients.join(', ')
                              : entry.recipients
                            : '\u2014'}{' '}
                          &middot; {formatDate(entry.sent_at || entry.created_at)}
                        </span>
                        {entry.status && (
                          <StatusChip
                            label={entry.status}
                            color={
                              entry.status === 'sent'
                                ? 'success'
                                : entry.status === 'failed'
                                  ? 'error'
                                  : 'neutral'
                            }
                            size="small"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          <p className={styles.drawerEmpty}>Invoice details not available</p>
        )}

        {/* Sticky Actions — outside scroll area */}
        {detail && (isDraft || isEditable || isRefundable) && (
          <div className={styles.drawerActions}>
            {isDraft && (
              <>
                <button
                  className={styles.primaryButton}
                  onClick={() => setActionDialog('approve')}
                  data-testid="action-approve"
                >
                  Approve & Send
                </button>
                <button
                  className={styles.secondaryButton}
                  onClick={() => setActionDialog('edit-draft')}
                  data-testid="action-edit-draft"
                >
                  Edit Draft
                </button>
                <button
                  className={styles.secondaryButton}
                  onClick={() => setActionDialog('cancel')}
                  data-testid="action-cancel-draft"
                >
                  Cancel Invoice
                </button>
              </>
            )}
            {isEditable && (
              <>
                <button
                  className={styles.primaryButton}
                  onClick={() => setActionDialog('mark-paid')}
                  data-testid="action-mark-paid"
                >
                  Mark Paid
                </button>
                <button
                  className={styles.secondaryButton}
                  onClick={() => setActionDialog('cancel')}
                  data-testid="action-cancel"
                >
                  Cancel Invoice
                </button>
                <button
                  className={styles.secondaryButton}
                  onClick={() => setResendConfirmOpen(true)}
                  disabled={resending}
                  data-testid="action-resend"
                >
                  {resending ? 'Sending...' : 'Resend Email'}
                </button>
              </>
            )}
            {isRefundable && (
              <button
                className={styles.dangerButton}
                onClick={() => setActionDialog('refund')}
                data-testid="action-refund"
              >
                Refund
              </button>
            )}
          </div>
        )}

        {/* Action Dialog */}
        <InvoiceActionDialog
          open={!!actionDialog}
          action={actionDialog}
          invoice={detail}
          onClose={() => setActionDialog(null)}
          onComplete={handleActionComplete}
        />

        {/* Resend Confirmation Dialog */}
        <Dialog
          open={resendConfirmOpen}
          onClose={() => setResendConfirmOpen(false)}
          maxWidth="xs"
          fullWidth
          data-testid="resend-confirm-dialog"
          className={styles.actionDialog}
        >
          <div className={styles.dialogHeader}>
            <span className={styles.dialogTitle}>Resend Invoice Email</span>
            <button
              className={styles.dialogClose}
              onClick={() => setResendConfirmOpen(false)}
              aria-label="Close"
            >
              &times;
            </button>
          </div>
          <DialogContent className={styles.dialogContent}>
            <p className={styles.refundHint}>
              Resend the invoice email for <strong>{detail?.invoice_number_local}</strong>?
            </p>
          </DialogContent>
          <div className={styles.dialogActions}>
            <button className={styles.cancelBtn} onClick={() => setResendConfirmOpen(false)}>
              Cancel
            </button>
            <button
              className={`${styles.confirmBtn} ${styles.confirmPrimary}`}
              onClick={handleResend}
            >
              Resend
            </button>
          </div>
        </Dialog>
      </div>
    </div>
  )
}

const CreateInvoiceDialog = ({ open, onClose, onComplete }) => {
  const [customers, setCustomers] = useState([])
  const [loadingCustomers, setLoadingCustomers] = useState(false)
  const [selectedCustomer, setSelectedCustomer] = useState('')
  const [currency, setCurrency] = useState('CAD')
  const [monthlyFee, setMonthlyFee] = useState('500')
  const [includedMinutes, setIncludedMinutes] = useState('200')
  const [overageRate, setOverageRate] = useState('1.50')
  const [actualMinutes, setActualMinutes] = useState('180')
  const [dueDays, setDueDays] = useState('30')
  const [autoFinalize, setAutoFinalize] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  const overageMinutes = Math.max(
    0,
    parseFloat(actualMinutes || 0) - parseFloat(includedMinutes || 0),
  )
  const hasOverage = overageMinutes > 0 && parseFloat(overageRate || 0) > 0

  React.useEffect(() => {
    if (!open) return
    setSelectedCustomer('')
    setCurrency('CAD')
    setMonthlyFee('500')
    setIncludedMinutes('200')
    setOverageRate('1.50')
    setActualMinutes('180')
    setDueDays('30')
    setAutoFinalize(true)
    setError(null)
    setLoadingCustomers(true)
    authenticatedFetchJson(apiEndpoints.billingCustomers)
      .then((res) => setCustomers(res?.data || []))
      .catch(() => setCustomers([]))
      .finally(() => setLoadingCustomers(false))
  }, [open])

  const handleSubmit = useCallback(async () => {
    if (!selectedCustomer) {
      setError('Please select a billing customer')
      return
    }
    if (isNaN(parseFloat(monthlyFee)) || parseFloat(monthlyFee) <= 0) {
      setError('Monthly fee must be positive')
      return
    }
    if (isNaN(parseFloat(includedMinutes)) || parseFloat(includedMinutes) < 0) {
      setError('Included minutes must be >= 0')
      return
    }
    if (isNaN(parseFloat(actualMinutes)) || parseFloat(actualMinutes) < 0) {
      setError('Actual minutes must be >= 0')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const res = await authenticatedFetchJson(apiEndpoints.billingInvoices, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          billing_customer_id: parseInt(selectedCustomer, 10),
          currency,
          monthly_fee: parseFloat(monthlyFee),
          included_minutes: parseFloat(includedMinutes),
          overage_rate: parseFloat(overageRate),
          actual_minutes: parseFloat(actualMinutes),
          due_days: parseInt(dueDays, 10) || 30,
          auto_finalize: autoFinalize,
        }),
      })
      onComplete(res?.data?.summary)
    } catch (err) {
      setError(err.message || 'Failed to create invoice')
    } finally {
      setSubmitting(false)
    }
  }, [
    selectedCustomer,
    currency,
    monthlyFee,
    includedMinutes,
    overageRate,
    actualMinutes,
    dueDays,
    autoFinalize,
    onComplete,
  ])

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      data-testid="create-invoice-dialog"
      className={styles.actionDialog}
    >
      <div className={styles.dialogHeader}>
        <span className={styles.dialogTitle}>Simulate Billing Cycle</span>
        <button
          className={styles.dialogClose}
          onClick={onClose}
          disabled={submitting}
          aria-label="Close"
        >
          &times;
        </button>
      </div>
      <DialogContent className={styles.dialogContent}>
        {/* Customer + Currency */}
        <div className={styles.fieldGroup}>
          <label className={styles.fieldLabel}>
            Billing Customer<span className={styles.fieldRequired}>*</span>
          </label>
          {loadingCustomers ? (
            <CircularProgress size={20} />
          ) : (
            <select
              className={styles.fieldInput}
              value={selectedCustomer}
              onChange={(e) => setSelectedCustomer(e.target.value)}
              data-testid="create-invoice-customer"
            >
              <option value="">Select a customer...</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.account_name || c.campaign_name || `Customer #${c.id}`}
                </option>
              ))}
            </select>
          )}
        </div>
        <div className={styles.fieldGroup}>
          <label className={styles.fieldLabel}>Currency</label>
          <select
            className={styles.fieldInput}
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            data-testid="create-invoice-currency"
          >
            <option value="CAD">CAD</option>
            <option value="USD">USD</option>
          </select>
        </div>

        {/* Mock Plan */}
        <div className={styles.fieldGroup}>
          <label className={styles.fieldLabel} style={{ fontWeight: 600, marginTop: 8 }}>
            Mock Plan Details
          </label>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel}>Monthly Fee ($)</label>
            <input
              className={styles.fieldInput}
              type="number"
              value={monthlyFee}
              onChange={(e) => setMonthlyFee(e.target.value)}
              min="0"
              step="0.01"
              data-testid="create-invoice-fee"
            />
          </div>
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel}>Included Min</label>
            <input
              className={styles.fieldInput}
              type="number"
              value={includedMinutes}
              onChange={(e) => setIncludedMinutes(e.target.value)}
              min="0"
              step="1"
              data-testid="create-invoice-included"
            />
          </div>
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel}>Overage $/min</label>
            <input
              className={styles.fieldInput}
              type="number"
              value={overageRate}
              onChange={(e) => setOverageRate(e.target.value)}
              min="0"
              step="0.01"
              data-testid="create-invoice-overage-rate"
            />
          </div>
        </div>

        {/* Mock Usage */}
        <div className={styles.fieldGroup}>
          <label className={styles.fieldLabel} style={{ fontWeight: 600, marginTop: 8 }}>
            Simulated Usage
          </label>
        </div>
        <div className={styles.fieldGroup}>
          <label className={styles.fieldLabel}>Actual Minutes Used</label>
          <input
            className={styles.fieldInput}
            type="number"
            value={actualMinutes}
            onChange={(e) => setActualMinutes(e.target.value)}
            min="0"
            step="1"
            data-testid="create-invoice-actual"
          />
          <span className={styles.fieldHint}>
            {hasOverage
              ? `${overageMinutes.toFixed(1)} min over plan \u2192 base + overage invoice ($${(overageMinutes * parseFloat(overageRate || 0)).toFixed(2)} overage)`
              : `Within plan \u2192 base invoice only`}
          </span>
        </div>

        {/* Options */}
        <div className={styles.fieldGroup}>
          <label className={styles.fieldLabel}>Due Days</label>
          <input
            className={styles.fieldInput}
            type="number"
            value={dueDays}
            onChange={(e) => setDueDays(e.target.value)}
            min="1"
            step="1"
            data-testid="create-invoice-due-days"
          />
        </div>
        <div className={styles.fieldGroup}>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={autoFinalize}
              onChange={(e) => setAutoFinalize(e.target.checked)}
              data-testid="create-invoice-auto-finalize"
            />
            Auto-finalize &amp; send emails
          </label>
          <span className={styles.fieldHint}>
            {autoFinalize
              ? `Will create ${hasOverage ? '2 invoices' : '1 invoice'}, finalize on Stripe, and send ${hasOverage ? '4 emails (2 Stripe + 2 dashboard)' : '2 emails (1 Stripe + 1 dashboard)'}`
              : 'Creates draft invoices only (no emails)'}
          </span>
        </div>

        {error && (
          <div className={styles.dialogError} data-testid="create-invoice-error">
            {error}
          </div>
        )}
      </DialogContent>
      <div className={styles.dialogActions}>
        <button className={styles.cancelBtn} onClick={onClose} disabled={submitting}>
          Cancel
        </button>
        <button
          className={`${styles.confirmBtn} ${styles.confirmPrimary}`}
          onClick={handleSubmit}
          disabled={submitting}
          data-testid="create-invoice-submit"
        >
          {submitting && (
            <CircularProgress size={14} style={{ marginRight: 8, color: 'inherit' }} />
          )}
          {autoFinalize ? `Create & Send (${hasOverage ? '2' : '1'})` : 'Create Draft'}
        </button>
      </div>
    </Dialog>
  )
}

const AdminBillingDashboard = ({ embedded = false }) => {
  const { stats, loading: statsLoading, error: statsError } = useBillingDashboardData()
  const { toast, showToast, closeToast } = useToast()
  const [statusFilter, setStatusFilter] = useState('all')
  const [typeFilter, setTypeFilter] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState('created_at')
  const [sortDir, setSortDir] = useState('desc')
  const [page, setPage] = useState(1)
  const [selectedInvoice, setSelectedInvoice] = useState(null)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [finalizeDialogOpen, setFinalizeDialogOpen] = useState(false)

  const {
    invoices,
    meta,
    loading: invoicesLoading,
    refetch,
  } = useBillingInvoiceData({
    status: statusFilter !== 'all' ? statusFilter : undefined,
    invoiceType: typeFilter !== 'all' ? typeFilter : undefined,
    page,
    limit: PAGE_SIZE,
    sortBy,
    sortDir,
  })

  const filteredInvoices = useMemo(() => {
    if (!searchTerm.trim()) return invoices
    const term = searchTerm.toLowerCase()
    return invoices.filter(
      (inv) =>
        inv.invoice_number_local?.toLowerCase().includes(term) ||
        inv.campaign_name?.toLowerCase().includes(term) ||
        inv.account_name?.toLowerCase().includes(term),
    )
  }, [invoices, searchTerm])

  const handleSort = useCallback(
    (field) => {
      setSortDir((prev) => (sortBy === field ? (prev === 'asc' ? 'desc' : 'asc') : 'desc'))
      setSortBy(field)
      setPage(1)
    },
    [sortBy],
  )

  const handleFilterChange = useCallback(
    (setter) => (value) => {
      setter(value)
      setPage(1)
    },
    [],
  )

  const handleExportCsv = useCallback(async () => {
    try {
      const params = new URLSearchParams()
      if (statusFilter !== 'all') params.set('status', statusFilter)
      if (typeFilter !== 'all') params.set('invoice_type', typeFilter)
      const url = `${apiEndpoints.billingInvoices}/export-csv?${params.toString()}`
      const response = await authenticatedFetch(url)
      if (!response.ok) throw new Error('Export failed')
      const blob = await response.blob()
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `invoices-${new Date().toISOString().split('T')[0]}.csv`
      a.click()
      URL.revokeObjectURL(a.href)
    } catch (err) {
      logger.error('CSV export failed:', err)
    }
  }, [statusFilter, typeFilter])

  const handleDrawerAction = useCallback(() => {
    setSelectedInvoice(null)
    refetch()
  }, [refetch])

  const handleCreateInvoiceComplete = useCallback(
    (summary) => {
      setCreateDialogOpen(false)
      refetch()
      if (summary) {
        showToast(
          `Created ${summary.invoices_created} invoice${summary.invoices_created > 1 ? 's' : ''} — Base: $${summary.base_total?.toFixed(2)}${summary.overage_total > 0 ? ` + Overage: $${summary.overage_total.toFixed(2)}` : ''}`,
          'success',
        )
      }
    },
    [refetch, showToast],
  )

  const handleFinalizeAllComplete = useCallback(() => {
    refetch()
  }, [refetch])

  if (statsLoading) {
    if (embedded) return <LoadingSpinner message="Loading billing dashboard..." />
    return (
      <PageLayout>
        <PageHeader title="Billing Dashboard" subtitle="Overview of billing and invoicing" />
        <LoadingSpinner message="Loading billing dashboard..." />
      </PageLayout>
    )
  }

  if (statsError) {
    if (embedded)
      return (
        <div className={styles.errorState}>
          <p>{statsError}</p>
        </div>
      )
    return (
      <PageLayout>
        <PageHeader title="Billing Dashboard" subtitle="Overview of billing and invoicing" />
        <div className={styles.errorState}>
          <p>{statsError}</p>
        </div>
      </PageLayout>
    )
  }

  const kpis = stats?.kpis || {}

  const content = (
    <>
      {/* Banner — visible only when BILLING_PAUSED=true on the API dyno */}
      <BillingPausedBanner />
      {/* KPI Cards */}
      <div className={styles.kpiGrid} data-testid="kpi-grid">
        <StatCard
          label="Total Billed"
          value={formatCurrency(kpis.total_billed_minor)}
          isRawText
          icon={<FontAwesomeIcon icon="file-invoice-dollar" />}
          size="small"
        />
        <StatCard
          label="Collected"
          value={formatCurrency(kpis.total_collected_minor)}
          isRawText
          icon={<FontAwesomeIcon icon="money-bill-wave" />}
          description={`${kpis.paid_count || 0} invoices`}
          size="small"
        />
        <StatCard
          label="Outstanding"
          value={formatCurrency(kpis.total_outstanding_minor)}
          isRawText
          icon={<FontAwesomeIcon icon="clock" />}
          description={`${kpis.outstanding_count || 0} invoices`}
          size="small"
        />
        <StatCard
          label="Overdue"
          value={formatCurrency(kpis.total_overdue_minor)}
          isRawText
          icon={<FontAwesomeIcon icon="exclamation-triangle" />}
          description={`${kpis.overdue_count || 0} invoices`}
          size="small"
        />
        {(kpis.draft_count || 0) > 0 && (
          <StatCard
            label="Pending Approval"
            value={formatCurrency(kpis.total_draft_minor)}
            isRawText
            icon={<FontAwesomeIcon icon="clipboard-check" />}
            description={`${kpis.draft_count} draft invoices`}
            size="small"
          />
        )}
      </div>

      {/* Aging Buckets */}
      {stats?.aging &&
        (stats.aging.bucket_1_30 > 0 ||
          stats.aging.bucket_31_60 > 0 ||
          stats.aging.bucket_61_90 > 0 ||
          stats.aging.bucket_90_plus > 0) && (
          <div className={styles.agingSection} data-testid="aging-section">
            <h3 className={styles.sectionTitle}>Overdue Aging</h3>
            <div className={styles.agingGrid}>
              <div className={styles.agingBucket}>
                <span className={styles.agingCount}>{stats.aging.bucket_1_30}</span>
                <span className={styles.agingLabel}>1-30 days</span>
              </div>
              <div className={styles.agingBucket}>
                <span className={styles.agingCount}>{stats.aging.bucket_31_60}</span>
                <span className={styles.agingLabel}>31-60 days</span>
              </div>
              <div className={styles.agingBucket}>
                <span className={styles.agingCount}>{stats.aging.bucket_61_90}</span>
                <span className={styles.agingLabel}>61-90 days</span>
              </div>
              <div className={styles.agingBucket}>
                <span className={styles.agingCount}>{stats.aging.bucket_90_plus}</span>
                <span className={styles.agingLabel}>90+ days</span>
              </div>
            </div>
          </div>
        )}

      {/* Monthly Trend */}
      {stats?.monthly_trend?.length > 0 && (
        <div className={styles.trendSection} data-testid="trend-section">
          <h3 className={styles.sectionTitle}>Monthly Revenue</h3>
          <div className={styles.trendGrid}>
            {stats.monthly_trend.map((m) => (
              <div key={m.month} className={styles.trendItem}>
                <span className={styles.trendMonth}>{m.month}</span>
                <span className={styles.trendTotal}>
                  {formatCurrency(parseInt(m.total_minor, 10))}
                </span>
                <div className={styles.trendBreakdown}>
                  <span>Base: {formatCurrency(parseInt(m.base_total_minor, 10))}</span>
                  <span>Overage: {formatCurrency(parseInt(m.overage_total_minor, 10))}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invoice Table */}
      <div className={styles.invoiceSection}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Invoices</h3>
          <div className={styles.sectionActions}>
            {(kpis.draft_count || 0) > 0 && (
              <button
                className={styles.primaryButton}
                onClick={() => setFinalizeDialogOpen(true)}
                data-testid="finalize-all-drafts-btn"
              >
                <FontAwesomeIcon icon="paper-plane" /> Finalize All Drafts ({kpis.draft_count})
              </button>
            )}
            <button
              className={styles.primaryButton}
              onClick={() => setCreateDialogOpen(true)}
              data-testid="create-invoice-btn"
            >
              <FontAwesomeIcon icon="plus" /> Create Invoice
            </button>
            <button
              className={styles.exportButton}
              onClick={handleExportCsv}
              data-testid="export-csv-btn"
            >
              <FontAwesomeIcon icon="download" /> Export CSV
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className={styles.filters}>
          <div className={styles.filterGroup}>
            <div className={styles.searchWrapper}>
              <FontAwesomeIcon icon="search" className={styles.searchIcon} />
              <input
                className={styles.searchInput}
                type="text"
                placeholder="Search invoices..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value)
                  setPage(1)
                }}
                data-testid="search-input"
              />
            </div>
            <label className={styles.filterLabel} htmlFor="inv-status-filter">
              Status:
            </label>
            <select
              className={styles.filterSelect}
              id="inv-status-filter"
              value={statusFilter}
              onChange={(e) => handleFilterChange(setStatusFilter)(e.target.value)}
              data-testid="status-filter"
            >
              {STATUS_FILTER_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt === 'all' ? 'All' : INVOICE_STATUS_LABELS[opt] || opt}
                </option>
              ))}
            </select>
            <label className={styles.filterLabel} htmlFor="inv-type-filter">
              Type:
            </label>
            <select
              className={styles.filterSelect}
              id="inv-type-filter"
              value={typeFilter}
              onChange={(e) => handleFilterChange(setTypeFilter)(e.target.value)}
              data-testid="type-filter"
            >
              {TYPE_FILTER_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt === 'all' ? 'All' : opt.charAt(0).toUpperCase() + opt.slice(1)}
                </option>
              ))}
            </select>
          </div>
          <span className={styles.resultCount}>{meta.total} total invoices</span>
        </div>

        {/* Table */}
        <div className={styles.tableWrapper}>
          {invoicesLoading ? (
            <LoadingSpinner message="Loading invoices..." />
          ) : (
            <table className={styles.table} data-testid="invoice-table">
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Client</th>
                  <th>Campaign</th>
                  <SortableHeader
                    field="invoice_type"
                    label="Type"
                    sortField={sortBy}
                    sortDirection={sortDir}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    field="status"
                    label="Status"
                    sortField={sortBy}
                    sortDirection={sortDir}
                    onSort={handleSort}
                  />
                  <th>Stripe ID</th>
                  <th>Method</th>
                  <th>Other</th>
                  <th>Platform Enh.</th>
                  <SortableHeader
                    field="total_minor"
                    label="Total"
                    sortField={sortBy}
                    sortDirection={sortDir}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    field="due_date"
                    label="Due Date"
                    sortField={sortBy}
                    sortDirection={sortDir}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    field="created_at"
                    label="Created"
                    sortField={sortBy}
                    sortDirection={sortDir}
                    onSort={handleSort}
                  />
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.length === 0 ? (
                  <tr>
                    <td colSpan={12} className={styles.emptyRow}>
                      {searchTerm || statusFilter !== 'all' || typeFilter !== 'all'
                        ? 'No matching invoices'
                        : 'No invoices found'}
                    </td>
                  </tr>
                ) : (
                  filteredInvoices.map((inv) => (
                    <tr
                      key={inv.id}
                      className={styles.clickableRow}
                      tabIndex={0}
                      role="button"
                      onClick={() => setSelectedInvoice(inv)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          setSelectedInvoice(inv)
                        }
                      }}
                      data-testid="invoice-row"
                    >
                      <td className={styles.invoiceNumber}>
                        {inv.invoice_number_local}
                        {inv.stripe_invoice_number &&
                          inv.stripe_invoice_number !== inv.invoice_number_local && (
                            <span className={styles.stripeNumber}>{inv.stripe_invoice_number}</span>
                          )}
                      </td>
                      <td>{inv.account_name || '\u2014'}</td>
                      <td>{inv.campaign_name || '\u2014'}</td>
                      <td>{inv.invoice_type}</td>
                      <td>
                        <StatusChip
                          label={INVOICE_STATUS_LABELS[inv.status] || inv.status}
                          color={INVOICE_STATUS_COLORS[inv.status] || 'neutral'}
                          size="small"
                        />
                      </td>
                      <td>
                        <StripeRailChip invoice={inv} />
                      </td>
                      <td>
                        {inv.paid_by
                          ? formatPaymentMethod(inv.paid_by)
                          : inv.status === 'paid'
                            ? 'Stripe'
                            : '\u2014'}
                      </td>
                      <td>
                        {inv.other_minor ? formatCurrency(inv.other_minor, inv.currency) : '\u2014'}
                      </td>
                      <td>
                        {inv.surcharge_minor
                          ? formatCurrency(inv.surcharge_minor, inv.currency)
                          : '\u2014'}
                      </td>
                      <td>
                        {formatCurrency(inv.total_minor, inv.currency)}
                        {inv.total_post_minor != null &&
                          inv.total_post_minor !== inv.total_minor && (
                            <span className={styles.postIncreaseBadge}>
                              {formatCurrency(inv.total_post_minor, inv.currency)} charged
                            </span>
                          )}
                        {inv.amount_refunded_minor > 0 && (
                          <span className={styles.refundedBadge}>
                            &minus;{formatCurrency(inv.amount_refunded_minor, inv.currency)}{' '}
                            refunded
                          </span>
                        )}
                      </td>
                      <td>{formatDate(inv.due_date)}</td>
                      <td>{formatDate(inv.created_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {meta.total_pages > 1 && (
          <div className={styles.pagination} data-testid="pagination">
            <button
              className={styles.pageButton}
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </button>
            <span className={styles.pageInfo}>
              Page {page} of {meta.total_pages}
            </span>
            <button
              className={styles.pageButton}
              disabled={page === meta.total_pages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Invoice Detail Drawer */}
      <InvoiceDetailDrawer
        invoice={selectedInvoice}
        onClose={() => setSelectedInvoice(null)}
        onAction={handleDrawerAction}
        showToast={showToast}
      />

      {/* Create Invoice Dialog */}
      <CreateInvoiceDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onComplete={handleCreateInvoiceComplete}
      />

      <FinalizeAllDraftsDialog
        open={finalizeDialogOpen}
        draftCount={kpis.draft_count || 0}
        onClose={() => setFinalizeDialogOpen(false)}
        onComplete={handleFinalizeAllComplete}
        showToast={showToast}
      />

      <ToastNotification
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={closeToast}
      />
    </>
  )

  if (embedded) return content

  return (
    <PageLayout>
      <PageHeader title="Billing Dashboard" subtitle="Overview of billing and invoicing" />
      {content}
    </PageLayout>
  )
}

AdminBillingDashboard.propTypes = {
  embedded: PropTypes.bool,
}


export default AdminBillingDashboard
